import React from 'react';
import { createRoot } from 'react-dom/client';
import injectManifoldCSS from '@manifoldxyz/css-reset';
import '@/styles/tailwind.scss';
import '@/styles/overrides.scss';
import { isRecognisedWidgetName, Widgets, WidgetsKeys } from '@/exported';
import { getAllChildren, toSnakeCase } from './common/util';
import WidgetWrapper from './components/WidgetWrapper';

const renderedComponents = new Map();
let widgetAttributeChangeObserver: MutationObserver;
let bodyChangeObserver: MutationObserver;

/**
 * @dev This is the main entrypoint of the widget. It gets called once the page has
 *      loaded. It finds all the widget divs and injects our React App into them.
 *      It also sets up a MutationObserver to listen for any new widget divs
 *      being added to the body of the DOM.
 */
const main = () => {
  // @dev: one MutationObserver gets reused by every rendered component
  widgetAttributeChangeObserver = new window.MutationObserver(handleWidgetAttributeChange);

  const elements = Object.keys(Widgets)
    .map((dataWidgetName) => {
      return [...document.querySelectorAll(`[data-widget=${dataWidgetName}]`)];
    })
    .flat();

  elements.forEach((widgetDiv) => {
    replaceWithWidget(widgetDiv as HTMLElement);
  });

  // @dev: Will listen for new widget divs being added/removed to the body of the DOM.
  // @dev: Will listen for divs dynamically adding the props as dataset attributes.
  const bodyNode = document.querySelector('body');
  if (bodyNode && !bodyChangeObserver) {
    const config = {
      attributeFilter: ['data-widget'],
      attributes: true,
      childList: true,
      subtree: true,
    };

    bodyChangeObserver = new window.MutationObserver(handleBodyChanges);
    bodyChangeObserver.observe(bodyNode, config);
  }
};

/**
 * @dev This block simply ensures that main() is only ran once the page has loaded.
 * @see main() for the rest of the flow
 */
if (window) {
  injectManifoldCSS(
    {
      // @dev: we don't need to reset styles since the tailwind preflight is already included
      reset: 'all',
      styles: 'vars',
    },
    // @dev: replace with the name of your package.json
    'claim-widgets',
  );

  const alreadyLoaded = performance
    .getEntriesByType('navigation')
    .every((e) => (e as PerformanceNavigationTiming).loadEventEnd);

  if (alreadyLoaded) {
    main();
  } else {
    window.addEventListener('load', main);
  }
}

/**
 * @param dataset some dataset from an HTMLElement with data-widget defined
 * @returns a typed object of props for the corresponding widget
 * @dev please keep the inferred return type here, do not try to specify it
 */
const convertDatasetToWidgetProps = (dataset: DOMStringMap) => {
  const widgetName = dataset.widget as WidgetsKeys;
  const widgetProps = Widgets[widgetName].defaultProps;
  const props: Record<string, unknown> = { ...widgetProps };
  const requiredKeys = Object.keys(widgetProps).filter(
    (key) => typeof widgetProps[key as keyof typeof widgetProps] !== 'undefined',
  );

  for (const key of Object.keys(dataset)) {
    if (!(key in widgetProps)) {
      continue;
    }

    const value = dataset[key];

    if (value === undefined && requiredKeys.includes(key)) {
      console.error(`Missing required prop: data-${toSnakeCase(key)}`);
    } else if (value !== undefined) {
      // Convert the dataset string based on the expected default prop type
      switch (typeof widgetProps[key as keyof typeof widgetProps]) {
        case 'string':
          props[key] = value;
          break;
        case 'number':
          try {
            props[key] = JSON.parse(value) as number;
          } catch (e) {
            console.error(`Invalid number passed to data-${toSnakeCase(key)}`);
          }
          break;
        case 'boolean':
          try {
            props[key] = JSON.parse(value) as boolean;
          } catch (e) {
            console.error(`Invalid boolean passed to data-${toSnakeCase(key)}`);
          }
          break;
        default:
          props[key] = new Function('return ' + value)();
      }
    }
  }

  return props as typeof widgetProps;
};

/**
 * @dev Checks if the el has a corresponding rendered React component in memory.
 *      If it does, unmounts it in React and destroys its data in app memory.
 */
const destroyPotentialWidget = (el: HTMLElement) => {
  const renderedComponentReactRoot = renderedComponents.get(el);
  if (renderedComponentReactRoot) {
    renderedComponentReactRoot.unmount();
    renderedComponents.delete(el);
  }
};

/**
 * @dev Replaces the element with its corresponding React component.
 *      Also attaches a MutationObserver to the element so we can listen for
 *      any attribute changes and rerender the component with the new data.
 *      We store the rendered component in memory so we can  unmount it later
 *      and destroy its data whenever changes are detected.
 *
 * @param element an HTMLElement with data-widget defined
 * @returns void
 */
const replaceWithWidget = (element: HTMLElement) => {
  if (!element.dataset.widget) {
    console.error('Malformed element passed to replaceWithWidget');
    return;
  }

  const widgetName = element.dataset.widget as WidgetsKeys;
  const typedProps = convertDatasetToWidgetProps(element.dataset);
  const root = createRoot(element);
  const Widget = Widgets[widgetName].component;

  root.render(
    <React.StrictMode>
      <WidgetWrapper widgetTag={widgetName}>
        <Widget {...typedProps} />
      </WidgetWrapper>
    </React.StrictMode>,
  );

  renderedComponents.set(element, root);

  // @dev: observe any attribute changes to the div and rerender on change
  const config = {
    attributes: true,
    childList: false,
    subtree: false,
  };
  widgetAttributeChangeObserver.observe(element, config);
};

/**
 * @dev This is triggered whenever the body changes to see if we need to
 *      render or destroy-then-render a widget into that added div
 *
 * We do this is 3 cases:
 * 1. If a div with data-widget was added to the DOM
 * 2. If a div with data-widget was removed from the DOM
 * 3. If a div dynamically had the data-widget attr added
 */
const handleBodyChanges = async (mutations: MutationRecord[]) => {
  mutations.forEach((mutation) => {
    // @dev: dynamically added a node that may have a div with our data-widget attribute inside
    mutation.addedNodes.forEach((node) => {
      const htmlEl = node as HTMLElement;

      if (isRecognisedWidgetName(htmlEl?.dataset?.widget)) {
        replaceWithWidget(htmlEl);
        return;
      }

      const children = getAllChildren(htmlEl);
      // @dev: safe to pop and skip the last element bc it is the root el which we've already checked above
      children.pop();

      children.forEach((child) => {
        if (isRecognisedWidgetName((child as HTMLElement)?.dataset?.widget)) {
          replaceWithWidget(child as HTMLElement);
        }
      });
    });

    // @dev: dynamically removed a node that had our widget rendered into it
    mutation.removedNodes.forEach((node) => {
      const htmlEl = node as HTMLElement;

      if (isRecognisedWidgetName(htmlEl?.dataset?.widget)) {
        destroyPotentialWidget(htmlEl);
        return;
      }

      const children = getAllChildren(htmlEl);
      children.forEach((child) => {
        if (isRecognisedWidgetName((child as HTMLElement)?.dataset?.widget)) {
          destroyPotentialWidget(child as HTMLElement);
        }
      });
    });

    // @dev: dynamically added the data-widget attribute
    if (mutation.type === 'attributes' && mutation.attributeName === 'data-widget') {
      const htmlEl = mutation.target as HTMLElement;
      if (isRecognisedWidgetName(htmlEl?.dataset?.widget)) {
        replaceWithWidget(htmlEl);
      }
    }
  });
};

/**
 * @dev This is triggered whenever a widget div has an attribute changed dynamically.
 *      We need to destroy the potential React widget that is already rendered inside that div.
 *      Then we render a new widget inside that div using the new dataset as props.
 */
const handleWidgetAttributeChange = async (mutations: MutationRecord[]) => {
  mutations.forEach((mutation) => {
    if (mutation.type === 'attributes' && mutation.attributeName !== 'data-v-app') {
      // destroy pre-existing app-component before replacing it with new one
      destroyPotentialWidget(mutation.target as HTMLElement);
      replaceWithWidget(mutation.target as HTMLElement);
    }
  });
};
